import { useState, useEffect, useRef, useCallback } from 'react';
import { useStore } from './store';
import { useChatStore } from './chatStore';
import { FlowEngine } from './engine';
import { Bot, User, Send, RefreshCw, X, Play, Upload, ChevronDown } from 'lucide-react';
import api from '../../utils/api';
import ReactMarkdown from 'react-markdown';

const ChatForm = ({ fields, onSubmit, isLast }) => {
  const [values, setValues] = useState({});
  if (!fields || fields.length === 0) return null;
  return (
    <div className={`space-y-3 bg-white p-3.5 rounded-xl border border-zinc-200 shadow-2xs ${!isLast ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="space-y-2.5">
        {fields.map((field) => (
          <div key={field.id} className="space-y-1">
            <label className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider pl-0.5">{field.label}</label>
            <input
              type={field.type}
              value={values[field.variableName] || ''}
              onChange={(e) => setValues({ ...values, [field.variableName]: e.target.value })}
              placeholder={field.placeholder || `Enter ${field.label}...`}
              className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 focus:border-zinc-900 focus:bg-white outline-none transition-all placeholder:text-zinc-400"
            />
          </div>
        ))}
      </div>
      <button
        onClick={() => onSubmit(values)}
        className="w-full py-2 bg-zinc-900 text-white rounded-lg text-xs font-medium hover:bg-zinc-800 transition-all shadow-2xs active:scale-[0.98]"
      >
        Submit Details
      </button>
    </div>
  );
};

const UploadWidget = ({ onFile, isLast }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }, [onFile]);

  if (!isLast) return null;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      className={`cursor-pointer border border-dashed rounded-xl p-4 flex flex-col items-center gap-2 transition-all ${
        isDragging ? 'border-zinc-400 bg-zinc-100' : 'border-zinc-300 bg-zinc-50/50 hover:border-zinc-400 hover:bg-zinc-100/50'
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
      />
      <div className="w-8 h-8 bg-zinc-100 rounded-lg flex items-center justify-center text-zinc-600">
        <Upload size={16} />
      </div>
      <div className="text-center">
        <p className="text-xs font-medium text-zinc-800">Drop image here or click to upload</p>
        <p className="text-[10px] text-zinc-400 mt-0.5">JPG, JPEG, PNG · Max 5MB</p>
      </div>
    </div>
  );
};

const SimJsonNode = ({ data, depth }) => {
  const [expanded, setExpanded] = useState(depth < 2);

  if (data === null || data === undefined) {
    return <span className="text-gray-500 italic text-[10px]">{String(data)}</span>;
  }
  if (typeof data !== 'object') {
    if (typeof data === 'string') return <span className="text-emerald-400 text-[10px]">&quot;{data.length > 60 ? data.slice(0, 60) + '…' : data}&quot;</span>;
    if (typeof data === 'boolean') return <span className="text-amber-400 text-[10px]">{String(data)}</span>;
    return <span className="text-sky-400 text-[10px]">{String(data)}</span>;
  }

  const isArray = Array.isArray(data);
  const entries = isArray ? data.map((v, i) => [String(i), v]) : Object.entries(data);
  if (entries.length === 0) return <span className="text-gray-500 text-[10px]">{isArray ? '[]' : '{}'}</span>;

  return (
    <div>
      <button onClick={() => setExpanded(p => !p)} className="inline-flex items-center gap-1 text-[10px] text-indigo-400 font-bold hover:text-indigo-300">
        <ChevronDown size={9} className={`transition-transform ${expanded ? '' : '-rotate-90'}`} />
        {isArray ? `[${entries.length}]` : `{${entries.length}}`}
      </button>
      {expanded && (
        <div className="ml-2.5 border-l border-gray-700 pl-1.5 space-y-0.5">
          {entries.map(([k, v]) => (
            <div key={k} className="text-[10px]">
              <span className="text-violet-400 font-bold">{isArray ? `[${k}]` : k}:</span>{' '}
              <SimJsonNode data={v} depth={depth + 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ApiResponseBubble = ({ data }) => {
  if (!data) return null;

  const isStandard = typeof data === 'object' && 'success' in data;
  const message = isStandard ? data.message : null;
  const payload = isStandard ? data.data : data;

  if (message && typeof message === 'string') {
    return <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap">{message}</p>;
  }

  if (payload && typeof payload === 'object') {
    return (
      <div className="rounded-xl bg-gray-950 border border-gray-800 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-800">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">API Response</span>
        </div>
        <div className="px-3 py-2 max-h-[200px] overflow-y-auto">
          <SimJsonNode data={payload} depth={0} />
        </div>
      </div>
    );
  }

  return <p className="text-sm font-medium leading-relaxed">{String(payload ?? '')}</p>;
};

export const Simulator = ({ onClose }) => {
  const { nodes, edges } = useStore();
  const { history, status, setHistory } = useChatStore();
  const [inputValue, setInputValue] = useState('');
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [defaultLogoUrl, setDefaultLogoUrl] = useState(null);
  const scrollRef = useRef(null);
  const engine = FlowEngine.getInstance();


  useEffect(() => {
    engine.updateGraph(nodes, edges);
  }, [nodes, edges, engine]);

  useEffect(() => {
    if (status !== 'paused' || !useChatStore.getState().sessionId) return;
    
    const sessionId = useChatStore.getState().sessionId;
    let socket = null;
    let reconnectTimeout = null;
    let shouldReconnect = true;

    const connectWebSocket = () => {
      if (status !== 'paused') return;

      const isSecureApi = (process.env.REACT_APP_API_BASE_URL || '').startsWith('https:');
      const protocol = isSecureApi ? 'wss:' : 'ws:';
      const wsHost = process.env.REACT_APP_API_BASE_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const aiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';
      const wsUrl = `${protocol}//${wsHost}/api/ws/widget?session_id=${sessionId}&ai_agent_id=${aiAgentId}`;

      console.log('🔌 Connecting Simulator WebSocket:', wsUrl);
      socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        console.log('✅ Simulator WebSocket Connected');
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📥 Simulator WS Message Received:', data);

          if (data.type === 'NEW_MESSAGE') {
            const role = data.message.role;
            if (role === 'assistant' || role === 'human_agent' || role === 'agent') {
              const exists = useChatStore.getState().history.some(m =>
                m.content === data.message.content &&
                (m.sender === 'bot' || m.sender === 'human_agent')
              );
              if (!exists) {
                useChatStore.getState().addHistoryItem({
                  id: Math.random().toString(36).substr(2, 9),
                  nodeId: 'websocket',
                  sender: 'bot',
                  type: 'text',
                  content: data.message.content,
                  timestamp: Date.now()
                });
              }
            }
          }
        } catch (err) {
          console.error('Error parsing simulator websocket message:', err);
        }
      };

      socket.onclose = () => {
        console.log('🔌 Simulator WebSocket Closed');
        if (shouldReconnect && status === 'paused') {
          reconnectTimeout = setTimeout(connectWebSocket, 3000);
        }
      };
    };

    connectWebSocket();

    return () => {
      shouldReconnect = false;
      if (socket) socket.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [status]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  useEffect(() => {
    const fetchWidgetConfig = async () => {
      try {
        const config = await api.getWidgetConfig();
        setWidgetConfig(config);
      } catch (err) {
        console.error("Failed to fetch widget config", err);
      }
    };
    fetchWidgetConfig();
  }, []);

  useEffect(() => {
    const fetchDefaultLogo = async () => {
      try {
        const data = await api.getCurrentLogo();
        if (data && data.logo_url) {
          setDefaultLogoUrl(data.logo_url);
        }
      } catch (err) {
        console.error("Failed to fetch default logo", err);
      }
    };
    fetchDefaultLogo();
  }, []);

  const handleStart = () => {
    const startNode = nodes.find(n => n.data?.type === 'start');
    if (startNode) engine.runFlow(startNode.id);
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;
    engine.handleUserInput(inputValue);
    setInputValue('');
  };

  const handleButton = (itemId, btnId, targetNodeId, label, url) => {
    const updatedHistory = history.map(item => {
      if (item.id === itemId) {
        return { ...item, clickedButtonId: btnId };
      }
      return item;
    });
    setHistory(updatedHistory);
    engine.handleButtonChoice(targetNodeId, label, url, btnId);
  };

  const handleFormSubmit = (values) => {
    engine.handleFormSubmit(values);
  };

  const handleFileUpload = useCallback((file) => {
    engine.handleImageUpload(file);
  }, [engine]);

  // Style normalizations based on configurations
  const borderRadius = widgetConfig?.border_radius || '24px';
  const headerBgColor = widgetConfig?.header_color || '#ffffff';
  const isDarkHeader = headerBgColor !== '#ffffff' && !headerBgColor.startsWith('rgba(255');
  const headerTextColor = isDarkHeader ? '#ffffff' : '#1e293b';

  return (
    <div 
      className="fixed bottom-6 right-6 w-[360px] h-[520px] max-h-[75vh] bg-white shadow-2xl border border-zinc-200/90 z-[9999] flex flex-col overflow-hidden animate-slideIn rounded-2xl"
      style={{ 
        fontFamily: widgetConfig?.font_family || 'Inter, sans-serif',
      }}
    >
      {/* Header */}
      <div 
        className="px-4 py-2.5 border-b border-zinc-200 flex items-center justify-between z-10 shrink-0 bg-white"
        style={{ 
          backgroundColor: headerBgColor !== '#ffffff' ? headerBgColor : '#ffffff',
          color: headerTextColor
        }}
      >
        <div className="flex items-center gap-2.5">
          {widgetConfig?.brand_logo_url || widgetConfig?.icon_url || defaultLogoUrl || '/default.png' ? (
            <img 
              src={widgetConfig?.brand_logo_url || widgetConfig?.icon_url || defaultLogoUrl || '/default.png'} 
              alt="Logo" 
              className="w-7 h-7 rounded-lg object-cover" 
            />
          ) : (
            <div 
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white shadow-2xs"
              style={{ backgroundColor: widgetConfig?.send_button_color || '#18181b' }}
            >
              <Bot size={15} />
            </div>
          )}
          <div>
            <h3 className="font-semibold tracking-tight text-xs text-zinc-900" style={{ color: headerTextColor }}>
              {widgetConfig?.title || 'Flow Preview'}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={handleStart} title="Restart session" className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors text-zinc-500 hover:text-zinc-900" style={{ color: headerTextColor }}>
            <RefreshCw size={14} />
          </button>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors text-zinc-500 hover:text-zinc-900" style={{ color: headerTextColor }}>
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Chat Body */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50/50 scroll-smooth custom-scrollbar">
        {status === 'idle' ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-3.5">
            <div className="w-12 h-12 bg-white rounded-2xl shadow-2xs border border-zinc-200 flex items-center justify-center text-zinc-900">
              <Play size={20} fill="currentColor" style={{ color: widgetConfig?.send_button_color || '#18181b' }} />
            </div>
            <div className="space-y-1">
              <h4 className="font-semibold text-zinc-900 text-sm">Ready to test?</h4>
              <p className="text-zinc-500 text-xs">Run your flow from start to finish interactively.</p>
            </div>
            <button 
              onClick={handleStart} 
              className="px-4 py-2 text-white font-medium transition-all active:scale-95 text-xs rounded-lg shadow-2xs hover:opacity-90"
              style={{ 
                backgroundColor: widgetConfig?.send_button_color || '#18181b',
              }}
            >
              Start Simulation
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {history.map((item, idx) => {
                if (item.sender === 'system') {
                  return (
                    <div key={item.id} className="flex justify-center my-2">
                      <span className="text-[10px] font-bold text-rose-500 bg-rose-50 border border-rose-100 rounded-full px-3 py-1 text-center max-w-[90%] leading-relaxed shadow-sm">
                        <ReactMarkdown className="inline">{item.content}</ReactMarkdown>
                      </span>
                    </div>
                  );
                }
                return (
                  <div
                    key={item.id}
                    className={`flex gap-2.5 ${item.sender === 'user' ? 'justify-end' : ''}`}
                  >
                    {item.sender === 'bot' && (
                      <div 
                        className="w-6 h-6 rounded-md flex items-center justify-center text-white shrink-0 shadow-2xs mt-0.5"
                        style={{ backgroundColor: widgetConfig?.send_button_color || '#18181b' }}
                      >
                        <Bot size={13} />
                      </div>
                    )}

                    <div className={`space-y-2 max-w-[85%] ${item.sender === 'user' ? 'items-end flex flex-col' : ''}`}>
                      {item.type === 'upload' ? (
                        <div 
                          className="border rounded-[18px] rounded-tr-none overflow-hidden max-w-[160px]"
                          style={{ 
                            backgroundColor: widgetConfig?.user_bubble_color || '#4f46e5',
                            borderColor: widgetConfig?.user_bubble_color || '#4f46e5'
                          }}
                        >
                          {item.uploadPreview && (
                            <img src={item.uploadPreview} alt={item.content} className="w-full h-auto object-cover max-h-36" />
                          )}
                          <p className="text-[9px] font-bold text-indigo-200 px-2.5 py-1.5 truncate">{item.content}</p>
                        </div>
                      ) : (
                        <div 
                          className="p-3 shadow-sm border"
                          style={{
                            backgroundColor: item.sender === 'bot' 
                              ? (widgetConfig?.bot_bubble_color || '#ffffff') 
                              : (widgetConfig?.user_bubble_color || '#4f46e5'),
                            color: item.sender === 'bot' 
                              ? '#1e293b' 
                              : '#ffffff',
                            borderColor: item.sender === 'bot' 
                              ? '#f1f5f9' 
                              : (widgetConfig?.user_bubble_color || '#4f46e5'),
                            borderRadius: borderRadius,
                            borderTopLeftRadius: item.sender === 'bot' ? '0px' : borderRadius,
                            borderTopRightRadius: item.sender === 'user' ? '0px' : borderRadius
                          }}
                        >
                          {item.type === 'text' && item.content === '__upload_widget__' ? (
                            <UploadWidget onFile={handleFileUpload} isLast={idx === history.length - 1 && status === 'paused'} />
                          ) : item.type === 'api_response' ? (
                            <ApiResponseBubble data={item.apiData} />
                          ) : item.type === 'image' && item.items?.[0] ? (
                            <div className="space-y-1.5">
                              <img src={item.items[0]} alt="Bot" className="rounded-lg w-full h-auto object-cover max-h-48" referrerPolicy="no-referrer" />
                              {item.content && <div className="text-xs font-medium leading-relaxed whitespace-pre-wrap pt-0.5"><ReactMarkdown className="inline-block">{item.content}</ReactMarkdown></div>}
                            </div>
                          ) : (item.type === 'text' || item.type === 'random_text' || item.type === 'button') ? (
                            <div className="text-xs font-medium leading-relaxed whitespace-pre-wrap">
                              <ReactMarkdown className="inline-block">{item.content || '...'}</ReactMarkdown>
                            </div>
                          ) : item.type === 'form' && item.formFields ? (
                            <ChatForm fields={item.formFields} onSubmit={handleFormSubmit} isLast={idx === history.length - 1} />
                          ) : null}
                        </div>
                      )}

                      {item.buttons && item.buttons.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                          {item.buttons.map(btn => {
                            const isClicked = item.clickedButtonId === btn.id;
                            const hasBeenClickedAny = !!item.clickedButtonId;
                            const isDisabled = hasBeenClickedAny || idx !== history.length - 1 || status !== 'paused';
                            return (
                              <button
                                key={btn.id}
                                disabled={isDisabled}
                                onClick={() => handleButton(item.id, btn.id, btn.targetNodeId, btn.label, btn.url)}
                                className={`px-4 py-2 border rounded-[16px] text-[10px] font-black transition-all shadow-sm ${
                                  isClicked 
                                    ? 'text-white' 
                                    : isDisabled 
                                      ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed opacity-60' 
                                      : 'bg-white border-indigo-100 hover:bg-slate-50 active:scale-95'
                                }`}
                                style={{ 
                                  color: isClicked 
                                    ? '#ffffff'
                                    : isDisabled
                                      ? undefined
                                      : (widgetConfig?.send_button_color || '#4f46e5'),
                                  borderColor: isClicked 
                                    ? (widgetConfig?.send_button_color || '#4f46e5')
                                    : isDisabled
                                      ? undefined
                                      : (widgetConfig?.send_button_color || '#e0e7ff'),
                                  backgroundColor: isClicked
                                    ? (widgetConfig?.send_button_color || '#4f46e5')
                                    : undefined
                                }}
                              >
                                {btn.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {item.sender === 'user' && (
                      <div className="w-6 h-6 rounded-md bg-zinc-900 text-white flex items-center justify-center shrink-0 shadow-2xs mt-0.5">
                        <User size={13} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {status === 'running' && (
              <div className="flex gap-2.5">
                <div 
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0"
                  style={{ backgroundColor: widgetConfig?.send_button_color || '#4f46e5' }}
                >
                  <Bot size={14} />
                </div>
                <div className="bg-white px-4 py-2.5 rounded-[18px] rounded-tl-none border border-slate-100 flex gap-1 items-center shadow-sm">
                  <div className="w-1.2 h-1.2 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <div className="w-1.2 h-1.2 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <div className="w-1.2 h-1.2 bg-slate-400 rounded-full animate-bounce" />
                </div>
              </div>
            )}

            {status === 'completed' && (
              <div className="text-center py-6 border-t border-slate-100">
                <p className="text-[9px] font-black text-slate-300 uppercase tracking-[0.2em]">End of Preview</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer Input */}
      <div className="p-3 bg-white border-t border-zinc-200 shrink-0">
        <div className="flex gap-1.5 bg-zinc-50 p-1 rounded-xl border border-zinc-200 focus-within:border-zinc-900 focus-within:bg-white transition-all">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type your message..."
            className="flex-1 bg-transparent border-none px-3 py-1 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            className="w-7 h-7 text-white rounded-lg flex items-center justify-center shadow-2xs transition-all active:scale-95 disabled:bg-zinc-200 disabled:text-zinc-400 disabled:shadow-none shrink-0 hover:opacity-90"
            style={{ 
              backgroundColor: widgetConfig?.send_button_color || '#18181b',
            }}
          >
            <Send size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};
